import React, { forwardRef } from "react";
import PropTypes from "kf-proptypes/widgets";

import { FieldLabel, FieldHint, FieldErrors } from "../fields/index.jsx";
import ReadonlyInput from "./readonly.jsx";
import EditableInput from "./editable.jsx";
import {
  ADDON_TYPE,
  SINGLELINE_INPUT_TYPE,
  SINGLELINE_SIZES,
  SINGLELINE_TYPE
} from "./constants.js";

export const SingleLine = forwardRef(function SingleLineComponent(
  {
    maxLength,
    name,
    id,
    placeholder,
    className,
    value,
    label,
    hint,
    errors,
    onClick,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    onKeyUp,
    type,
    readonly,
    required,
    disabled,
    leftIcon,
    rightIcon,
    autoFocus,
    styleType,
    size,
    onEnter,
    wrapperClassName,
    isError,
    handleReadonlyLink,
    readonlyWrap,
    iconRender,
    addonType,
    labelLeftIcon,
    labelRightIcon,
    multiLine,
    ...restProps
  },
  ref
) {
  return (
    <div>
      {label && (
        <FieldLabel
          disabled={disabled}
          label={label}
          targetId={id}
          required={required}
          leftIcon={labelLeftIcon}
          rightIcon={labelRightIcon}
          size={size}
        />
      )}
      {readonly ? (
        <ReadonlyInput
          value={value}
          className={className}
          handleReadonlyLink={handleReadonlyLink}
          wrapperClassName={wrapperClassName}
          isError={isError}
          errors={errors}
          disabled={disabled}
          readonlyWrap={readonlyWrap}
          styleType={styleType}
          addonType={addonType}
          size={size}
          multiLine={multiLine}
          id={id}
        />
      ) : (
        <EditableInput
          onClick={onClick}
          name={name}
          placeholder={placeholder}
          onChange={onChange}
          disabled={disabled}
          onBlur={onBlur}
          onFocus={onFocus}
          id={id}
          value={value}
          required={required}
          wrapperClassName={wrapperClassName}
          className={className}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          type={type}
          prefix={leftIcon}
          suffix={rightIcon}
          autoFocus={autoFocus}
          onPressEnter={onEnter}
          ref={ref}
          iconRender={iconRender}
          size={size}
          isError={isError}
          errors={errors}
          styleType={styleType}
          addonType={addonType}
          {...restProps}
        />
      )}
      {hint && <FieldHint hint={hint} targetId={id} />}
      {errors && errors.length > 0 && (
        <FieldErrors errors={errors} targetId={id} />
      )}
    </div>
  );
});

export {
  ADDON_TYPE,
  SINGLELINE_INPUT_TYPE,
  SINGLELINE_SIZES,
  SINGLELINE_TYPE,
  ReadonlyInput,
  EditableInput
};

SingleLine.propTypes = {
  /** maximum length allowed */
  maxLength: PropTypes.string,
  /** value which user enter */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** Placeholder for input field */
  placeholder: PropTypes.string,
  /** Custom class name from consumer side */
  className: PropTypes.string,
  /** Required is mandatory field */
  required: PropTypes.bool,
  /** unique id for input field */
  id: PropTypes.string,
  /** label for input field */
  label: PropTypes.string,
  /** hint for input field */
  hint: PropTypes.string,
  /** errors which will show error */
  errors: PropTypes.arrayOf(PropTypes.string),
  /** onClick enables click for user */
  onClick: PropTypes.func,
  /** name is unique props for input field */
  name: PropTypes.string,
  /** readonly prop mentions whether input need to readonly or not */
  readonly: PropTypes.bool,
  /** disabled prop indicates whether input filed is disabled or not */
  disabled: PropTypes.bool,
  /** indicates blur on input filed */
  onBlur: PropTypes.func,
  /** onchange will trigger when user changes any data in input field */
  onChange: PropTypes.func,
  /** onfocus will trigger when user clicks on input filed */
  onFocus: PropTypes.func,
  /** when user click and enters any key in keyboard */
  onKeyDown: PropTypes.func,
  /** when user click and enters any key in keyboard */
  onKeyUp: PropTypes.func,
  /** onEnter will trigger when user clicks enter in keyboard */
  onEnter: PropTypes.func,
  /** indicated type of input filed whether text or password */
  type: PropTypes.string,
  /** it will render left icon of input field */
  leftIcon: PropTypes.node,
  /** it will render right icon of input field */
  rightIcon: PropTypes.node,
  /** autofocus */
  autoFocus: PropTypes.bool,
  /** styletype is whether input is normal or minimal indication */
  styleType: PropTypes.string,
  /** size indicates for input filed whether it will be medium etc */
  size: PropTypes.string,
  /** className */
  wrapperClassName: PropTypes.string,
  /** bool to indicate error or not */
  isError: PropTypes.bool,
  /** handleReadonlyLink */
  handleReadonlyLink: PropTypes.bool,
  readonlyWrap: PropTypes.bool,
  /** iconRender funcion for input field */
  iconRender: PropTypes.func,
  /** addonType funcion for input field */
  addonType: PropTypes.string,
  /** it will render left icon of label field */
  labelLeftIcon: PropTypes.node,
  /** it will render right icon of label field */
  labelRightIcon: PropTypes.node,
  multiLine: PropTypes.bool
};

SingleLine.defaultProps = {
  placeholder: undefined,
  autoFocus: false,
  onChange: () => {},
  onBlur: () => {},
  onKeyDown: () => {},
  onKeyUp: () => {},
  onEnter: () => {},
  iconRender: () => {},
  className: "",
  labelClassName: "",
  value: "",
  errors: [],
  type: SINGLELINE_INPUT_TYPE.TEXT,
  styleType: SINGLELINE_TYPE.NORMAL,
  size: SINGLELINE_SIZES.BASE,
  wrapperClassName: "",
  handleReadonlyLink: false,
  readonlyWrap: false,
  addonType: ADDON_TYPE.NORMAL
};
